import React, { useState } from 'react';
import {
  Settings,
  Mail,
  Shield,
  Server,
  Upload,
  Zap,
  Database,
  Globe,
  Lock,
  Activity,
  FileText,
  HelpCircle
} from 'lucide-react';

const SettingsGuide = ({ onClose }) => {
  const [currentSection, setCurrentSection] = useState('overview');

  const sections = [
    {
      id: 'overview',
      title: 'Settings Overview',
      icon: Settings,
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Settings className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Smart Email Verifier Settings</h2>
            <p className="text-gray-600">Configure your email verification system for optimal performance and security.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800/50">
              <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                Email Configuration
              </h3>
              <ul className="text-blue-700 dark:text-blue-200 space-y-2 text-sm">
                <li>• <strong>SMTP Settings</strong> - Configure email sending</li>
                <li>• <strong>IMAP Settings</strong> - Set up email receiving</li>
                <li>• <strong>Email Scanner</strong> - Customize verification process</li>
                <li>• <strong>Disposable Domains</strong> - Manage blocked domains</li>
              </ul>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800/50">
              <h3 className="font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                Security & License
              </h3>
              <ul className="text-green-700 dark:text-green-200 space-y-2 text-sm">
                <li>• <strong>License Management</strong> - Verify your license</li>
                <li>• <strong>System Security</strong> - Configure CORS and logs</li>
                <li>• <strong>API Access</strong> - Manage API tokens</li>
                <li>• <strong>Rate Limiting</strong> - Control verification speed</li>
              </ul>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800/50">
              <h3 className="font-semibold text-purple-800 dark:text-purple-300 mb-3 flex items-center">
                <Upload className="w-5 h-5 mr-2" />
                Upload & Processing
              </h3>
              <ul className="text-purple-700 dark:text-purple-200 space-y-2 text-sm">
                <li>• <strong>File Upload Limits</strong> - Set size restrictions</li>
                <li>• <strong>Concurrency Settings</strong> - Control processing speed</li>
                <li>• <strong>Worker Configuration</strong> - Optimize background jobs</li>
                <li>• <strong>Bulk Scan Settings</strong> - Schedule automated scans</li>
              </ul>
            </div>

            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800/50">
              <h3 className="font-semibold text-orange-800 dark:text-orange-300 mb-3 flex items-center">
                <Database className="w-5 h-5 mr-2" />
                Templates & Data
              </h3>
              <ul className="text-orange-700 dark:text-orange-200 space-y-2 text-sm">
                <li>• <strong>Verification Templates</strong> - Custom email templates</li>
                <li>• <strong>Error Strings</strong> - Manage bounce patterns</li>
                <li>• <strong>Role-based Emails</strong> - Configure business emails</li>
                <li>• <strong>Data Management</strong> - Export and backup</li>
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-gray-800 p-4 rounded-lg border border-yellow-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              <HelpCircle className="w-5 h-5 mr-2" />
              Getting Started
            </h3>
            <p className="text-gray-700 dark:text-gray-200 text-sm">
              Start with the SMTP configuration for basic email verification. Then explore other settings based on your needs.
              All settings are saved automatically when you make changes.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'smtp',
      title: 'SMTP Configuration',
      icon: Mail,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-800/50">
            <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              SMTP Settings Explained
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-blue-700 dark:text-blue-200 mb-2">Required Settings</h4>
                <ul className="text-blue-600 dark:text-blue-200 space-y-1">
                  <li>• <strong>Host</strong> - SMTP server address (e.g., smtp.gmail.com)</li>
                  <li>• <strong>Port</strong> - SMTP port (587 for TLS, 465 for SSL)</li>
                  <li>• <strong>Username</strong> - Your email address</li>
                  <li>• <strong>Password</strong> - App password or regular password</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">Advanced Settings</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• <strong>Secure</strong> - Enable SSL/TLS encryption</li>
                  <li>• <strong>Timeout</strong> - Connection timeout in milliseconds</li>
                  <li>• <strong>Concurrency</strong> - Number of simultaneous connections</li>
                  <li>• <strong>Rate Limit</strong> - Emails per minute limit</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-800/50">
            <h3 className="font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center">
              <Zap className="w-5 h-5 mr-2" />
              Popular SMTP Providers
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-green-700 dark:text-green-200 mb-2">Gmail</h4>
                <ul className="text-green-600 dark:text-green-200 space-y-1">
                  <li>• Host: smtp.gmail.com</li>
                  <li>• Port: 587 (TLS) or 465 (SSL)</li>
                  <li>• Username: your-email@gmail.com</li>
                  <li>• Password: App password required</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Outlook/Hotmail</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• Host: smtp-mail.outlook.com</li>
                  <li>• Port: 587 (TLS)</li>
                  <li>• Username: your-email@outlook.com</li>
                  <li>• Password: Regular password</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Yahoo</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• Host: smtp.mail.yahoo.com</li>
                  <li>• Port: 587 (TLS) or 465 (SSL)</li>
                  <li>• Username: your-email@yahoo.com</li>
                  <li>• Password: App password required</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Custom Server</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• Host: Your SMTP server</li>
                  <li>• Port: Usually 25, 587, or 465</li>
                  <li>• Username: Your email or username</li>
                  <li>• Password: Your password</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-gray-800 p-4 rounded-lg border border-yellow-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              <HelpCircle className="w-5 h-5 mr-2" />
              Pro Tips
            </h3>
            <ul className="text-gray-800 dark:text-gray-100 space-y-2 text-sm">
              <li>• <strong>Use App Passwords</strong> - For Gmail and Yahoo, generate app passwords instead of using your main password</li>
              <li>• <strong>Test Connection</strong> - Always test your SMTP settings before using them</li>
              <li>• <strong>Rate Limits</strong> - Start with conservative rate limits (100 emails/minute) and adjust based on your provider</li>
              <li>• <strong>Concurrency</strong> - Higher concurrency = faster verification but may trigger spam filters</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'imap',
      title: 'IMAP Configuration',
      icon: Mail,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-800/50">
            <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              IMAP Settings Explained
            </h3>
            <p className="text-blue-700 dark:text-blue-200 text-sm mb-4">
              IMAP (Internet Message Access Protocol) is used for advanced email verification methods.
              It allows the system to check if emails actually exist by attempting to connect to the mail server.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">Required Settings</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• <strong>Host</strong> - IMAP server address</li>
                  <li>• <strong>Port</strong> - IMAP port (993 for SSL, 143 for non-SSL)</li>
                  <li>• <strong>Username</strong> - Your email address</li>
                  <li>• <strong>Password</strong> - Your email password</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">Security Settings</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• <strong>TLS</strong> - Enable for secure connections</li>
                  <li>• <strong>SSL</strong> - Use SSL encryption</li>
                  <li>• <strong>Authentication</strong> - Usually 'PLAIN' or 'LOGIN'</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-800/50">
            <h3 className="font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center">
              <Zap className="w-5 h-5 mr-2" />
              Popular IMAP Providers
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Gmail</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• Host: imap.gmail.com</li>
                  <li>• Port: 993 (SSL)</li>
                  <li>• TLS: Enabled</li>
                  <li>• Username: your-email@gmail.com</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Outlook/Hotmail</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• Host: outlook.office365.com</li>
                  <li>• Port: 993 (SSL)</li>
                  <li>• TLS: Enabled</li>
                  <li>• Username: your-email@outlook.com</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Yahoo</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• Host: imap.mail.yahoo.com</li>
                  <li>• Port: 993 (SSL)</li>
                  <li>• TLS: Enabled</li>
                  <li>• Username: your-email@yahoo.com</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Custom Server</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• Host: Your IMAP server</li>
                  <li>• Port: Usually 143 or 993</li>
                  <li>• TLS: Check with your provider</li>
                  <li>• Username: Your email or username</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-2 flex items-center">
              <Activity className="w-5 h-5 mr-2" />
              When to Use IMAP
            </h3>
            <ul className="text-purple-700 space-y-2 text-sm">
              <li>• <strong>Advanced Verification</strong> - For more accurate email validation</li>
              <li>• <strong>Business Emails</strong> - When verifying corporate email addresses</li>
              <li>• <strong>High Accuracy Required</strong> - When you need the most reliable results</li>
              <li>• <strong>Custom Domains</strong> - For verifying emails on your own domain</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'upload',
      title: 'Upload & Processing',
      icon: Upload,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-800/50">
            <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center">
              <Upload className="w-5 h-5 mr-2" />
              Upload Settings Explained
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">File Limits</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• <strong>Max Upload Size</strong> - Maximum file size in MB</li>
                  <li>• <strong>Max Email List Size</strong> - Maximum emails per file</li>
                  <li>• <strong>Supported Formats</strong> - CSV, TXT files</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">Processing Settings</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• <strong>Import Concurrency</strong> - Files processed simultaneously</li>
                  <li>• <strong>Max Concurrent Uploads</strong> - Uploads at once</li>
                  <li>• <strong>Worker Concurrency</strong> - Background job workers</li>
                  <li>• <strong>SMTP Concurrency</strong> - Email verification threads</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-800/50">
            <h3 className="font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center">
              <Zap className="w-5 h-5 mr-2" />
              Performance Optimization
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-green-700 mb-2">For Small Lists (1K-10K emails)</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• Import Concurrency: 2-4</li>
                  <li>• SMTP Concurrency: 5-10</li>
                  <li>• Worker Concurrency: 2</li>
                  <li>• Expected Speed: 100-500 emails/minute</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-green-700 mb-2">For Large Lists (10K+ emails)</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• Import Concurrency: 4-8</li>
                  <li>• SMTP Concurrency: 10-20</li>
                  <li>• Worker Concurrency: 4-8</li>
                  <li>• Expected Speed: 500-2000 emails/minute</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-gray-800 p-4 rounded-lg border border-yellow-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              <HelpCircle className="w-5 h-5 mr-2" />
              Best Practices
            </h3>
            <ul className="text-gray-700 dark:text-gray-200 space-y-2 text-sm">
              <li>• <strong>Start Conservative</strong> - Begin with lower concurrency and increase gradually</li>
              <li>• <strong>Monitor Resources</strong> - Watch CPU and memory usage</li>
              <li>• <strong>Split Large Files</strong> - Break files &gt;100K emails into smaller chunks</li>
              <li>• <strong>Use Background Processing</strong> - Large files are processed in the background</li>
              <li>• <strong>Check SMTP Limits</strong> - Don't exceed your email provider's rate limits</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'security',
      title: 'Security & License',
      icon: Shield,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-800/50">
            <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              License Management
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">License Information</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• <strong>License Key</strong> - Your purchase code</li>
                  <li>• <strong>Domain</strong> - Registered domain</li>
                  <li>• <strong>IP Address</strong> - Server IP address</li>
                  <li>• <strong>Status</strong> - Active/Inactive</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">System Security</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• <strong>CORS Origin</strong> - Allowed domains</li>
                  <li>• <strong>Log Level</strong> - Debugging information</li>
                  <li>• <strong>API Tokens</strong> - Secure API access</li>
                  <li>• <strong>Session Security</strong> - Cookie settings</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-800/50">
            <h3 className="font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center">
              <Lock className="w-5 h-5 mr-2" />
              Security Best Practices
            </h3>
            <ul className="text-green-700 dark:text-green-200 space-y-2 text-sm">
              <li>• <strong>Use HTTPS</strong> - Always use SSL/TLS in production</li>
              <li>• <strong>Strong Passwords</strong> - Use complex passwords for all accounts</li>
              <li>• <strong>App Passwords</strong> - Use app-specific passwords for email services</li>
              <li>• <strong>Regular Updates</strong> - Keep the system updated</li>
              <li>• <strong>Monitor Logs</strong> - Check logs regularly for suspicious activity</li>
              <li>• <strong>Backup Data</strong> - Regular backups of verification results</li>
            </ul>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800/50">
            <h3 className="font-semibold text-purple-800 dark:text-purple-300 mb-2 flex items-center">
              <Globe className="w-5 h-5 mr-2" />
              CORS Configuration
            </h3>
            <p className="text-purple-700 dark:text-purple-200 text-sm mb-3">
              CORS (Cross-Origin Resource Sharing) controls which domains can access your API.
            </p>
            <ul className="text-purple-700 space-y-1 text-sm">
              <li>• <strong>Development</strong> - http://localhost:3000</li>
              <li>• <strong>Production</strong> - https://yourdomain.com</li>
              <li>• <strong>Multiple Domains</strong> - Separate with commas</li>
              <li>• <strong>Wildcards</strong> - Use * for all domains (not recommended for production)</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'templates',
      title: 'Templates & Data',
      icon: FileText,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-800/50">
            <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Verification Templates
            </h3>
            <p className="text-blue-700 dark:text-blue-200 text-sm mb-4">
              Verification templates are custom email messages used during the verification process.
              They help determine if an email address is valid by attempting to send a test message.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">Template Components</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• <strong>Subject Line</strong> - Email subject</li>
                  <li>• <strong>Message Body</strong> - Email content (HTML supported)</li>
                  <li>• <strong>From Address</strong> - Sender email</li>
                  <li>• <strong>Reply-To</strong> - Reply address</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">Best Practices</h4>
                <ul className="text-blue-600 space-y-1">
                  <li>• <strong>Professional Tone</strong> - Use business-like language</li>
                  <li>• <strong>Clear Purpose</strong> - Explain why you're contacting them</li>
                  <li>• <strong>Unsubscribe Option</strong> - Include unsubscribe link</li>
                  <li>• <strong>Company Information</strong> - Include your company details</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 p-5 rounded-lg border border-green-200 dark:border-green-800/50">
            <h3 className="font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center">
              <Database className="w-5 h-5 mr-2" />
              Error Strings & Bounce Management
            </h3>
            <p className="text-green-700 dark:text-green-200 text-sm mb-4">
              Error strings help identify bounced emails and categorize them properly.
              The system uses these patterns to determine if an email is invalid.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Common Bounce Types</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• <strong>Hard Bounce</strong> - Email doesn't exist</li>
                  <li>• <strong>Soft Bounce</strong> - Temporary delivery failure</li>
                  <li>• <strong>Blocked</strong> - Server rejected email</li>
                  <li>• <strong>Spam Filter</strong> - Caught by spam filters</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-green-700 mb-2">Error String Examples</h4>
                <ul className="text-green-600 space-y-1">
                  <li>• "User not found"</li>
                  <li>• "Mailbox does not exist"</li>
                  <li>• "Recipient address rejected"</li>
                  <li>• "550 5.1.1" (SMTP error codes)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-gray-800 p-4 rounded-lg border border-yellow-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              <HelpCircle className="w-5 h-5 mr-2" />
              Template Tips
            </h3>
            <ul className="text-gray-700 dark:text-gray-200 space-y-2 text-sm">
              <li>• <strong>Test Templates</strong> - Always test templates before using them</li>
              <li>• <strong>Keep It Simple</strong> - Avoid complex HTML or images</li>
              <li>• <strong>Include Contact Info</strong> - Add your company contact details</li>
              <li>• <strong>Comply with Laws</strong> - Follow CAN-SPAM and GDPR requirements</li>
              <li>• <strong>Monitor Bounces</strong> - Regularly check bounce reports</li>
            </ul>
          </div>
        </div>
      )
    }
  ];

  const currentSectionData = sections.find(s => s.id === currentSection);

  // Lock body scroll when modal is open
  React.useEffect(() => {
    document.body.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  return (
    <div className="modal-overlay">
      <div className="bg-white dark:bg-gray-900 dark:text-gray-100 rounded-2xl shadow-2xl max-w-6xl w-full my-8 max-h-[calc(100vh-4rem)] overflow-hidden border border-gray-100 dark:border-gray-800">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 sticky top-0">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Settings Guide</h1>
              <p className="text-blue-100 mt-1">Complete guide to configuring your email verifier</p>
            </div>
            <button
              onClick={onClose}
              className="text-blue-100 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 overflow-y-auto max-h-[calc(100vh-12rem)]">
            <nav className="p-4">
              <ul className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <li key={section.id}>
                      <button
                        onClick={() => setCurrentSection(section.id)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center ${currentSection === section.id
                          ? 'bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800/50'
                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5'
                          }`}
                      >
                        <Icon className="w-5 h-5 mr-3" />
                        {section.title}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto max-h-[calc(100vh-12rem)]">
            <div className="p-6">
              {currentSectionData?.content}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsGuide; 